import React, { useState, useEffect, useCallback } from 'react';
import { useParams, generatePath } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import { useStoreUnitActions } from 'src/redux/helpers';
import { WidgetConfig } from 'src/components/widgets/widget';
import solutionsStore from 'src/pages/Solutions/redux';
import {
  API_DASHBOARD_WIDGET_CREATE,
  API_DASHBOARD_WIDGET_UPDATE,
  API_DATA_ACCESS_POST_API,
} from 'src/pages/Solutions/constants';

import { ChartConfigurationForm } from './ChartConfigurationForm';
import { KustoQueryForm } from './KustoQueryForm';
import { WidgetLayoutForm } from './WidgetLayoutForm';
import {
  getDefaultWidgetConfig,
  prepareWidgetPayload,
  validateWidgetConfig,
} from '../utils';
import { chartTypeOptions } from '../../constant';

interface ReusableWidgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (widget: WidgetConfig) => void;
  widget?: WidgetConfig;
  existingWidgets: WidgetConfig[];
  dashboardId?: string;
  twinId?: string;
}

export const ReusableWidgetConfigModal: React.FC<
  ReusableWidgetConfigModalProps
> = ({
  isOpen,
  onClose,
  onSave,
  widget,
  existingWidgets,
  dashboardId,
  twinId,
}) => {
    const { solutionId } = useParams();
    const { showAlert } = useAlert();
    const actions = useStoreUnitActions(solutionsStore);

    const filterColumns = (columns: string[]): string[] =>
      columns.filter(
        (col) =>
          col !== 'originalTelemetry' &&
          col !== 'telemetry' &&
          col.trim() !== '' &&
          col !== 'undefined' &&
          col !== 'null'
      );

    const [config, setConfig] = useState(
      () => widget || getDefaultWidgetConfig(solutionId || '', existingWidgets)
    );

    const [isExecutingQuery, setIsExecutingQuery] = useState(false);
    const [availableColumns, setAvailableColumns] = useState<string[]>([]);
    const [queryError, setQueryError] = useState<string>('');
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [triggerValidation, setTriggerValidation] = useState(false);

    const isEditMode = !!widget;

    useEffect(() => {
      if (widget && isOpen) {
        setConfig({
          ...widget,
          dataSource: {
            type: widget.dataSource?.type || 'apiInternal',
            method: widget.dataSource?.method || 'POST',
            endpoint:
              widget.dataSource?.endpoint ||
              `/api/v1/data/solutions/${solutionId}/tenants/1/executerawkustoquery`,
            headers: widget.dataSource?.headers || [],
            body: widget.dataSource?.body || '',
            refreshRate: widget.dataSource?.refreshRate || 30,
          },
          chartConfiguration: {
            xAxis: {
              columnName: widget.chartConfiguration?.xAxis?.columnName || '',
              label: widget.chartConfiguration?.xAxis?.label || '',
            },
            yAxis: {
              columnName: widget.chartConfiguration?.yAxis?.columnName || '',
              label: widget.chartConfiguration?.yAxis?.label || '',
            },
            dataSeries: widget.chartConfiguration?.dataSeries || [],
            showLegend: widget.chartConfiguration?.showLegend ?? true,
            selectedColumns: widget.chartConfiguration?.selectedColumns || [],
          },
          layout: {
            width: widget.layout?.width || 6,
            height: widget.layout?.height || 4,
            order: widget.layout?.order || 1,
          },
        });

        if (widget.dataSource?.body?.trim()) {
          setTimeout(async () => {
            try {
              setIsExecutingQuery(true);
              setQueryError('');

              const response = await pepApiRequest({
                method: RequestMethod.Post,
                url: generatePath(API_DATA_ACCESS_POST_API, { solutionId }),
                params: { query: widget.dataSource.body },
              });

              const data = response;

              if (
                data &&
                data.Success &&
                data.Results &&
                Array.isArray(data.Results) &&
                data.Results.length > 0
              ) {
                const transformedResults = data.Results.map((item) => {
                  if (item.telemetry && typeof item.telemetry === 'object') {
                    return {
                      ...item,
                      ...item.telemetry,
                      originalTelemetry: item.telemetry,
                    };
                  }
                  if (
                    item.telemetry !== undefined &&
                    item.telemetry !== null &&
                    item.telemetry !== 'telemetry'
                  ) {
                    // If telemetry is a primitive value (string, number, etc.), keep it as a separate field
                    return {
                      ...item,
                      telemetryValue: item.telemetry,
                      originalTelemetry: item.telemetry,
                    };
                  }

                  return item;
                }).filter((item) => {
                  if (!item || typeof item !== 'object') {
                    return false;
                  }

                  if (item.telemetry === 'telemetry') {
                    return false;
                  }

                  return true;
                });

                const firstValidResult = transformedResults.find(
                  (result) => result && typeof result === 'object'
                );

                if (firstValidResult) {
                  const columns = Object.keys(firstValidResult);
                  const filteredColumns = filterColumns(columns);
                  setAvailableColumns(filteredColumns);
                }
              } else if (data && Array.isArray(data) && data.length > 0) {
                const columns = Object.keys(data[0]);
                setAvailableColumns(filterColumns(columns));
              } else if (
                data &&
                data.data &&
                Array.isArray(data.data) &&
                data.data.length > 0
              ) {
                const columns = Object.keys(data.data[0]);
                setAvailableColumns(filterColumns(columns));
              } else {
                const existingColumns = new Set<string>();
                if (widget.chartConfiguration?.xAxis?.columnName) {
                  existingColumns.add(widget.chartConfiguration.xAxis.columnName);
                }
                if (widget.chartConfiguration?.yAxis?.columnName) {
                  existingColumns.add(widget.chartConfiguration.yAxis.columnName);
                }
                widget.chartConfiguration?.dataSeries?.forEach((series) => {
                  if (series.columnName) {
                    existingColumns.add(series.columnName);
                  }
                });
                setAvailableColumns(Array.from(existingColumns));
              }
            } catch (error) {
              const existingColumns = new Set<string>();
              if (widget.chartConfiguration?.xAxis?.columnName) {
                existingColumns.add(widget.chartConfiguration.xAxis.columnName);
              }
              if (widget.chartConfiguration?.yAxis?.columnName) {
                existingColumns.add(widget.chartConfiguration.yAxis.columnName);
              }
              widget.chartConfiguration?.dataSeries?.forEach((series) => {
                if (series.columnName) {
                  existingColumns.add(series.columnName);
                }
              });
              setAvailableColumns(Array.from(existingColumns));
            } finally {
              setIsExecutingQuery(false);
            }
          }, 100);
        }
      } else if (!widget && isOpen) {
        setConfig(getDefaultWidgetConfig(solutionId || '', existingWidgets));
        setAvailableColumns([]); // Reset columns for new widget
      }

      if (isOpen) {
        setQueryError('');
        setIsExecutingQuery(false);
        setFieldErrors({});
        if (!widget) setAvailableColumns([]); // Always reset columns on open for new widget
      }
    }, [widget, isOpen, existingWidgets, solutionId]);

    useEffect(() => {
      if (isOpen) {
        setTriggerValidation(false);
      }
    }, [isOpen]);

    const executeKustoQuery = useCallback(async () => {
      if (!config.dataSource?.body?.trim()) {
        setFieldErrors((prev) => ({ ...prev, query: 'Please enter a query' }));
        return;
      }

      setIsExecutingQuery(true);
      setFieldErrors((prev) => ({ ...prev, query: '' }));

      try {
        const response = await pepApiRequest({
          method: RequestMethod.Post,
          url: generatePath(API_DATA_ACCESS_POST_API, { solutionId }),
          params: { query: config.dataSource.body },
        });

        const data = response;

        if (
          data &&
          data.Success &&
          data.Results &&
          Array.isArray(data.Results) &&
          data.Results.length > 0
        ) {
          const transformedResults = data.Results.map((item) => {
            if (item.telemetry && typeof item.telemetry === 'object') {
              return {
                ...item,
                ...item.telemetry,
                originalTelemetry: item.telemetry,
              };
            }
            if (
              item.telemetry !== undefined &&
              item.telemetry !== null &&
              item.telemetry !== 'telemetry'
            ) {
              return {
                ...item,
                telemetryValue: item.telemetry,
                originalTelemetry: item.telemetry,
              };
            }

            return item;
          }).filter((item) => {
            if (!item || typeof item !== 'object') {
              return false;
            }

            if (item.telemetry === 'telemetry') {
              return false;
            }

            return true;
          });

          const firstValidResult = transformedResults.find(
            (result) => result && typeof result === 'object'
          );

          if (firstValidResult) {
            const columns = Object.keys(firstValidResult);
            const filteredColumns = filterColumns(columns);
            setAvailableColumns(filteredColumns);
          } else {
            setFieldErrors((prev) => ({
              ...prev,
              query: 'No valid data found in Results array after transformation',
            }));
            setAvailableColumns([]);
          }
        } else if (data && !data.Success && data.Error) {
          setFieldErrors((prev) => ({
            ...prev,
            query: `API Error: ${data.Error}`,
          }));
          setAvailableColumns([]);
        } else if (data && Array.isArray(data) && data.length > 0) {
          const columns = Object.keys(data[0]);
          setAvailableColumns(filterColumns(columns));
        } else if (
          data &&
          data.data &&
          Array.isArray(data.data) &&
          data.data.length > 0
        ) {
          const columns = Object.keys(data.data[0]);
          setAvailableColumns(filterColumns(columns));
        } else {
          setFieldErrors((prev) => ({
            ...prev,
            query: 'No data returned from query or unexpected response format',
          }));
          setAvailableColumns([]);
        }
      } catch (error) {
        setFieldErrors((prev) => ({
          ...prev,
          query:
            error instanceof Error ? error.message : 'Failed to execute query',
        }));
        setAvailableColumns([]);
      } finally {
        setIsExecutingQuery(false);
      }
    }, [config.dataSource?.body, solutionId]);

    const [handleSave, isSaving] = useStatefulApi(
      () => {
        setTriggerValidation(true);
        setFieldErrors({});
        const errors: { [key: string]: string } = {};

        if (!config.title?.trim()) {
          errors.title = 'Widget title is required';
        }

        if (!config.chartType?.trim()) {
          errors.chartType = 'Chart type is required';
        }

        if (!config.dataSource?.body?.trim()) {
          errors.query = 'Kusto query is required';
        }

        const refreshRate = config.dataSource?.refreshRate || 30;
        if (refreshRate < 5) {
          errors.refreshRate = 'Refresh rate must be at least 5 seconds';
        } else if (refreshRate > 3600) {
          errors.refreshRate = 'Refresh rate must not exceed 3600 seconds';
        }

        if (config.chartType !== 'table') {
          if (!config.chartConfiguration?.xAxis?.columnName) {
            errors.xAxisColumn =
              config.chartType === 'metric'
                ? 'Value column is required'
                : 'X-axis column is required';
          }

          if (
            config.chartType !== 'pie' &&
            config.chartType !== 'radar' &&
            config.chartType !== 'metric' &&
            !config.chartConfiguration?.yAxis?.columnName
          ) {
            errors.yAxisColumn = 'Y-axis column is required';
          }

          if (
            config.chartType !== 'pie' &&
            config.chartType !== 'metric' &&
            (!config.chartConfiguration?.dataSeries ||
              config.chartConfiguration.dataSeries.length === 0)
          ) {
            errors.dataSeries = 'At least one data series is required';
          } else if (
            config.chartType !== 'pie' &&
            config.chartType !== 'metric' &&
            config.chartConfiguration?.dataSeries
          ) {
            const invalidSeries = config.chartConfiguration.dataSeries.some(
              (series) => !series.columnName
            );
            if (invalidSeries) {
              errors.dataSeries = 'All data series must have a column selected';
            }
          }
        }

        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          return Promise.reject(new Error('Validation failed'));
        }

        const validation = validateWidgetConfig(config);

        if (!validation.isValid) {
          setFieldErrors({ general: validation.error || 'Validation failed' });
          return Promise.reject(new Error('Validation failed'));
        }
        const isUpdating = !!widget && !!widget.id;

        if (isUpdating) {
          const apiUrl = generatePath(API_DASHBOARD_WIDGET_UPDATE, {
            solutionId,
            dashboardId: dashboardId || 'default',
            widgetId: widget.id,
            twinId,
          });

          return pepApiRequest({
            method: RequestMethod.Put,
            url: apiUrl,
            params: prepareWidgetPayload(config),
          });
        }

        const apiUrl = generatePath(API_DASHBOARD_WIDGET_CREATE, {
          solutionId,
          dashboardId: dashboardId || 'default',
          twinId,
        });

        return pepApiRequest({
          method: RequestMethod.Post,
          url: apiUrl,
          params: prepareWidgetPayload(config),
        });
      },
      (response) => {
        const isUpdating = !!widget && !!widget.id;
        showAlert({
          type: AlertType.Success,
          title: isUpdating ? 'Widget Updated' : 'Widget Created',
          text: `Widget "${config.title || 'Untitled Widget'}" has been ${isUpdating ? 'updated' : 'created'
            } successfully`,
        });

        const savedConfig: WidgetConfig =
          isUpdating && widget?.id
            ? { ...config, id: widget.id }
            : { ...config, id: response?.id || `widget-${Date.now()}` };
        onSave(savedConfig);

        onClose();
        actions.WidgetList({ solutionId, dashboardId, twinId });
      }
    );

    const handleChartTypeChange = (chartType: string) => {
      setConfig({
        ...config,
        chartType,
      });
    };

    const canSaveWidget = () => {
      if (!config.title?.trim() || !config.dataSource?.body?.trim()) {
        return false;
      }

      if (config.chartType === 'table') {
        return true;
      }


      if (isEditMode) {
        return !!(config.chartConfiguration?.dataSeries?.length > 0);
      }


      return !!(
        config.chartConfiguration?.xAxis?.columnName &&
        config.chartConfiguration?.dataSeries?.length > 0
      );
    };

    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {isEditMode ? 'Edit Widget' : 'Add Widget'}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                p: 0.5,
                ml: 'auto',
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Widget Title"
              value={config.title || ''}
              onChange={(e) => {
                setConfig((prev) => ({ ...prev, title: e.target.value }));
                if (fieldErrors.title) {
                  setFieldErrors((prev) => ({ ...prev, title: '' }));
                }
              }}
              placeholder="Enter widget title"
              fullWidth
              required
              disabled={isEditMode}
              error={!!fieldErrors.title}
              helperText={fieldErrors.title || ''}
            />

            <FormControl
              fullWidth
              disabled={isEditMode}
              error={!!fieldErrors.chartType}
            >
              <InputLabel required>Chart Type</InputLabel>
              <Select
                value={config.chartType || 'line'}
                label="Chart Type"
                onChange={(e) => {
                  handleChartTypeChange(e.target.value);
                  // Clear error when user selects
                  if (fieldErrors.chartType) {
                    setFieldErrors((prev) => ({ ...prev, chartType: '' }));
                  }
                }}
                disabled={isEditMode}
                required
              >
                {chartTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.chartType && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {fieldErrors.chartType}
                </Typography>
              )}
            </FormControl>

            <KustoQueryForm
              query={config.dataSource?.body || ''}
              onQueryChange={(query) => {
                const newConfig = { ...config };
                if (newConfig.dataSource) {
                  newConfig.dataSource.body = query;
                }
                setConfig(newConfig);

                setAvailableColumns([]);

                if (fieldErrors.query) {
                  setFieldErrors((prev) => ({ ...prev, query: '' }));
                }
              }}
              onExecuteQuery={executeKustoQuery}
              isExecuting={isExecutingQuery}
              availableColumns={availableColumns}
              queryError={fieldErrors.query || ''}
              solutionId={solutionId || ''}
              isEditMode={isEditMode}
              onClearChartConfig={
                isEditMode
                  ? () => {
                    setConfig((prevConfig) => ({
                      ...prevConfig,
                      chartConfiguration: {
                        ...prevConfig.chartConfiguration!,
                        xAxis: { columnName: '', label: '' },
                        yAxis: { columnName: '', label: '' },
                        dataSeries: [],
                        selectedColumns: [],
                      },
                    }));
                  }
                  : undefined
              }
            />

            <ChartConfigurationForm
              chartType={config.chartType || 'line'}
              chartConfiguration={config.chartConfiguration!}
              availableColumns={availableColumns}
              triggerValidation={triggerValidation}
              onChartConfigChange={(chartConfiguration) =>
                setConfig((prev) => ({ ...prev, chartConfiguration }))
              }
              onValidationChange={(isValid, errors) => {
                setFieldErrors((prev) => ({
                  ...prev,
                  ...errors,
                }));
              }}
            />

            <TextField
              label="Refresh Rate (seconds)"
              type="number"
              value={config.dataSource?.refreshRate || 30}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10) || 30;
                const newConfig = { ...config };
                if (newConfig.dataSource) {
                  newConfig.dataSource.refreshRate = value;
                }
                setConfig(newConfig);

                if (value < 5) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    refreshRate: 'Refresh rate must be at least 5 seconds',
                  }));
                } else if (value > 3600) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    refreshRate: 'Refresh rate must not exceed 3600 seconds',
                  }));
                } else {
                  setFieldErrors((prev) => ({ ...prev, refreshRate: '' }));
                }
              }}
              inputProps={{ min: 5, max: 3600 }}
              fullWidth
              error={!!fieldErrors.refreshRate}
              helperText={fieldErrors.refreshRate}
            />

            <WidgetLayoutForm
              layout={config.layout!}
              onLayoutChange={(layout) =>
                setConfig((prev) => ({ ...prev, layout }))
              }
              existingWidgets={existingWidgets}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isEditMode ? 'Update Widget' : 'Add Widget'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
